export interface ExperienceType {
  image_src?: string;
  title: string;
  description: string;
  organization?: string;
  period?: string;
  position?: string;
  link?: string;
  github?: string;
  documentLink?: string;
  mobileTitle?: string;
  mobileDescription?: string;
  mobileOrganization?: string;
}

export interface ExperiencesType {
  type: 'educational' | 'business' | 'personal';
  experiences: ExperienceType[];
}

export const experiences: ExperiencesType[] = [
  {
    type: 'business',
    experiences: [
      {
        // image_src: '/images/projects_webp/ai_document_processing.webp',
        title: 'AI Document Processing Platform',
        description:
          'Built backend functionality for a cloud-based document processing platform that turns uploaded business documents into structured data. Developed REST APIs with FastAPI, designed PostgreSQL models, set up asynchronous processing with Celery and Redis, integrated AWS S3 for document storage, and connected the processing pipeline to LLM-based data extraction.',
        organization: 'NDA',
        period: '07.2026 - 09.2026',
        position: 'Fullstack / AI Product Engineer',
      },
      {
        // image_src: '/images/projects_webp/ai_knowledge.webp',
        title: 'AI Knowledge Platform',
        description:
          'Built backend functionality for an AI-powered knowledge platform where users can upload documents and search their contents using natural language. Developed REST APIs and authentication, designed PostgreSQL models, and implemented document processing, embeddings, retrieval pipelines, and RAG-based answer generation.',
        organization: 'NDA',
        period: '04.2026 - 06.2026',
        position: 'Fullstack / AI Product Engineer',
      },
      {
        image_src: '/images/projects_webp/taskmvp.webp',
        title: 'TaskMVP',
        description:
          'Contributed to a production marketplace connecting customers with local service providers in Finland. Built authentication flows including Google OAuth, task creation and search, real-time chat, OpenStreetMap-based location features, and payment-related functionality. Also worked with Supabase, Resend, REST APIs, and responsive interfaces across desktop and mobile.',
        organization: 'Mbnö Oy',
        period: '01.2026 - 03.2026',
        position: 'Fullstack Product Developer',
      },
      {
        image_src: '/images/projects_webp/ace-webapp.webp',
        title: 'Ace Friends',
        description:
          'Built backend services and internal tools for a production matchmaking platform. Designed REST APIs, implemented background jobs with Celery and Redis, integrated AWS S3 for media storage, and developed administration tools. Also contributed to deployment, infrastructure, and frontend features as the product evolved.',
        organization: 'Ace Friends',
        period: '07.2025 - 01.2026',
        position: 'Fullstack Product Developer',
        // link: 'https://webapp.acetest.site/',
      },
      {
        image_src: '/images/projects_webp/pgs.webp',
        title: 'Ace PGS',
        description:
          'Built a browser-based multiplayer game inspired by GeoGuessr, using PUBG map panoramas as the game environment. Developed the core gameplay and scoring systems, player rankings, backend APIs, and frontend interfaces, including the real-time interactions required during gameplay.',
        organization: 'Ace Friends',
        period: '07.2025 - 01.2026',
        position: 'Fullstack Developer',
        // link: 'https://pgs.acetest.site/',
      },
      {
        image_src: '/images/projects_webp/web_terminal_market_trillion.webp',
        title: 'Trillion',
        description:
          'Developed core interfaces for an AI-powered investment platform, covering Home, Portfolio, Trading, and Agents. Built responsive data-heavy views, integrated backend APIs, and implemented the state management and interaction logic behind key product workflows across desktop and mobile.',
        organization: 'Entangle',
        period: '12.2025 - 02.2026',
        position: 'Frontend Engineer',
        // link: 'https://trillion.xyz/',
      },
      {
        image_src: '/images/projects_webp/xyber.webp',
        title: 'Xyber',
        description:
          'Developed frontend functionality for an AI agents platform connected to blockchain infrastructure. Built agent management interfaces, token swap flows, search and dashboard features, as well as responsive product pages and landing pages. Worked with backend APIs and Web3 integrations across the product, while also contributing to related interfaces for KOL Worth, Mechanet, and Dock Market.',
        organization: 'Entangle',
        period: '02.2025 - 01.2026',
        position: 'Frontend Engineer',
        // link: 'https://xyber.inc/',
      },
      {
        image_src: '/images/projects_webp/genome.webp',
        title: 'Genome',
        description:
          'Developed frontend features for a Web3 tournament platform, including onboarding, tournament management, match views, and dynamic data-driven interfaces. Integrated product features with backend services and focused on responsive behaviour and clear user flows.',
        organization: 'Entangle',
        period: '02.2025 - 05.2025',
        position: 'Frontend Engineer',
        // link: 'https://genomeprotocol.com/',
      },
      {
        image_src: '/images/projects_webp/xybot.webp',
        title: 'Xybot',
        description:
          'Developed frontend functionality for an AI-powered gaming platform with blockchain integrations. Built dynamic game interfaces, wallet connectivity, real-time interactions, and API integrations for the main product workflows.',
        organization: 'Entangle',
        period: '02.2025 - 05.2025',
        position: 'Frontend Engineer',
      },
      {
        image_src: '/images/projects_webp/tee.webp',
        title: 'TEE Attestation Explorer',
        description:
          'Built a specialized web interface for exploring and verifying Trusted Execution Environment attestation data. Implemented search, filtering, data visualization, and interfaces for working with large and technically complex datasets.',
        organization: 'Entangle',
        period: '02.2025 - 05.2025',
        position: 'Frontend Engineer',
        // link: 'https://prove.xyber.inc/',
      },
      {
        image_src: '/images/projects_webp/poker.webp',
        title: 'AI-Poker',
        description:
          'Developed frontend functionality for an AI-powered poker tournament platform with NFT and POAP integrations. Built collection browsing, ownership tracking, and dynamic tournament interfaces while connecting blockchain data to the product experience.',
        organization: 'Entangle',
        period: '02.2025 - 05.2025',
        position: 'Frontend Engineer',
      },
      {
        image_src: '/images/projects_webp/omsu.webp',
        title: 'Online School OMSU',
        description:
          'Developed a full-stack educational platform for online courses, payments, video calls, and real-time communication. Built backend APIs, authentication, payment flows, chat and WebSocket functionality, and contributed to deployment and infrastructure.',
        organization: 'OMSU',
        period: '09.2024 - 12.2024',
        position: 'Fullstack Developer',
      },
      {
        image_src: '/images/projects_webp/factoring_preview.webp',
        title: 'Factoring for Business',
        description:
          'Built a responsive corporate website for an international factoring company with Next.js. Developed the main page layouts and interactive frontend components, with a focus on performance, responsive behaviour, and collaboration with a designer and animator.',
        organization: 'Personal',
        period: '08.2024',
        position: 'Frontend Developer',
      },
      {
        image_src: '/images/projects_webp/virtual_library.webp',
        title: 'Virtual Library',
        description:
          'Developed a full-stack platform for managing and discovering books, audiobooks, and other digital media. Built catalog management, search and filtering, user profiles, bookmarks, and recommendation features, working across the frontend and backend as the platform grew.',
        organization: 'Private Company',
        period: '04.2024 - 08.2024',
        position: 'Fullstack Developer',
      },
      {
        image_src: '/images/projects_webp/megamailer.webp',
        title: 'Mega Mailer',
        description:
          'Led frontend development of an automated email distribution system. Coordinated development tasks, built the application interface, and integrated the frontend with a Django backend and MySQL database.',
        organization: 'Mega Mailer',
        period: '07.2024',
        position: 'Frontend Developer',
      },
      {
        image_src: '/images/projects_webp/milanikas.webp',
        title: "Sisters' Artists Portfolio",
        description:
          'Built a responsive multilingual portfolio for two artists with a custom image gallery. Implemented three-language support, optimized image loading, and adapted the experience for both desktop and mobile devices.',
        organization: 'Personal',
        period: '06.2024',
        position: 'Frontend Developer',
        link: 'https://milanikas.alwaysdata.net/',
      },
      {
        image_src: '/images/projects_webp/table_test.webp',
        title: 'Testing Management Tool',
        description:
          'Built an internal testing management system with multiple account types and authentication. Implemented test creation, configurable test rules, date selection, data sampling and shuffling, row management, result saving with comments, and automatic calculation of key test metrics. Built the frontend with React and the backend with Node.js.',
        organization: 'Cloudronics',
        period: '02.2024 - 04.2024',
        position: 'Fullstack Developer · Internship',
        documentLink: '/docs/Recommendation letter.pdf',
      },
      {
        image_src: '/images/projects_webp/content_editor.webp',
        title: 'Product Data & Content Tools',
        description:
          'Built several internal tools for working with product data and content. Implemented product search and filtering, API-driven product information including prices, discounts, expiry dates, and recommendations, product addon selection, and a content editor for adding subtitles and product links to videos. Also developed a separate web scraping tool with Python.',
        organization: 'Cloudronics',
        period: '02.2024 - 04.2024',
        position: 'Fullstack Developer · Internship',
        documentLink: '/docs/Recommendation letter.pdf',
      },
      {
        // image_src: '/images/projects_webp/education_platform.webp',
        title: 'Educational Platform',
        description:
          'Worked on an educational platform for managing courses and learning content. Started with React and TypeScript, building course pages, forms, reusable components, and responsive interfaces, then gradually took on backend development with Django REST Framework and PostgreSQL, including authentication, user roles, and course management.',
        organization: 'Private Client',
        period: '01.2023 - 01.2024',
        position: 'Junior Web Developer → Fullstack Developer',
      },
    ],
  },
  {
    type: 'educational',
    experiences: [
      {
        image_src: '/images/projects_webp/afrikan_tahti.webp',
        title: 'Afrikan Tähti',
        description:
          'Developed a web-based version of the Finnish board game from scratch. Implemented the game logic, multiplayer mode, and progress saving, using React and TypeScript for the frontend, Flask for the backend, and MariaDB for data storage.',
        organization: 'Metropolia',
        period: '08.2024 - 09.2024',
        position: 'Fullstack Developer',
        link: 'https://chebanik.alwaysdata.net/afrikan_tahti/',
        github: 'https://github.com/CHchisho/Afrikan_Tahti-React',
      },
      {
        image_src: '/images/projects_webp/yripolku.webp',
        title: "The Entrepreneur's Path",
        description:
          'Designed, developed, and launched a responsive website for a large-scale educational college event. Worked independently on the interface and frontend implementation and prepared the website to handle a large number of visitors.',
        period: '01.2024',
        organization: 'Luksia',
        position: 'Frontend Developer',
        documentLink: '/docs/LY_stipendi_Luksia_2024.pdf',
      },
    ],
  },
  {
    type: 'personal',
    experiences: [
      {
        image_src: '/images/projects_webp/petodo.webp',
        title: 'Petodo · Pet Project',
        description:
          'Designed and developed a mobile productivity application combining task management with gamification and virtual pets. Built the backend architecture, REST APIs, progression systems, and task workflows, and handled deployment and the Google Play release.',
        position: 'Fullstack Developer',
        // link: '[https://petodo.io/](https://petodo.io/)',
      },
      {
        image_src: '/images/projects_webp/knucklebones.webp',
        title: 'Knucklebones · Pet Project',
        description:
          'Built a multiplayer web game from scratch with online multiplayer, local two-player mode, and an AI opponent. Developed the React frontend, Express backend, networking, and core game logic, including support for simultaneous game sessions.',
        period: '01.2024 - 02.2024',
        position: 'Fullstack Developer',
        link: 'https://chebanik.alwaysdata.net/knucklebones/',
      },
    ],
  },
];

export const education: ExperienceType[] = [
  {
    title: 'Bachelor of Engineering, Information and Communication Technology',
    mobileTitle: 'Bachelor of Engineering',
    organization: 'Metropolia, University of Applied Sciences',
    period: '2025',
    description: 'One of the best University of Applied Sciences in Helsinki, Finland',
  },
  {
    title: 'Vocation Qualification in Information and Communication Technology, software developer',
    mobileTitle: 'ICT Software Developer',
    organization: 'Luksia, Länsi-Uudenmaan koulutuskuntayhtymä',
    mobileOrganization: 'Luksia',
    period: '2024',
    description:
      'Internships and interesting projects during studies, with a focus on React, TypeScript and FastAPI. I completed my studies externally, as I quickly passed all exams and received my diploma.',
  },
];

// 06.2025 - 08.2025 Virtual Library
// 01.2025 BookFlex
// 04.2024 - 05.2024 GearHub

// {
//   image_src: '/images/projects_webp/afrikan_tahti.webp',
//   title: 'Afrikan Tähti',
//   description:
//     'Developed a full-featured web version of a popular Finnish board game from scratch. Implemented complex game logic, multiplayer mode, and progress saving system. The project demonstrates mastery of the full technology stack: React TypeScript for frontend, Python Flask for backend, and MariaDB for data management.',
//   period: '08.2024 - 09.2024',
//   position: 'Fullstack Developer',
//   organization: 'Metropolia',
//   link: 'https://chebanik.alwaysdata.net/afrikan_tahti/',
//   github: 'https://github.com/CHchisho/Afrikan_Tahti-React',
// },

// {
//   image_src: '/images/projects_webp/table_test.webp',
//   title: 'Testing Tool',
//   description:
//     'Developed a corporate testing management system with a complete cycle: test creation, data entry, automatic calculations, and administrative panel. Implemented authentication system, real-time data synchronization between users, and secure storage on Node.js server. The project improved testing efficiency in the company and received positive feedback from the team.',
//   period: '02.2024 - 04.2024',
//   organization: 'Cloudronics',
//   position: 'Frontend Developer · Internship',
//   documentLink: '/docs/Recommendation_Ilia.docx',
// },
// {
//   image_src: '/images/projects_webp/product_page.webp',
//   title: 'Product Page',
//   description:
//     'Created a product page with interactive image gallery, detailed feature descriptions, similar product recommendation system, and customer review integration. Optimized user experience to increase sales, implemented responsive design, and ensured fast page loading.',
//   period: '02.2024 - 04.2024',
//   organization: 'Cloudronics',
//   position: 'Frontend Developer · Internship',
//   link: 'https://chebanik.alwaysdata.net/product_page/',
// },

// {
//   image_src: '/images/projects_webp/task_manager.webp',
//   title: 'Simple Task Manager',
//   description:
//     'Created an intuitive task management application with drag-and-drop functionality and local data storage. Implemented CRUD operations, element reordering, and optimized performance for working with large numbers of tasks. The project demonstrates attention to UX/UI details and understanding of modern web development principles.',
//   period: '01.2024',
//   position: 'Frontend Developer',
//   link: 'https://chebanik.alwaysdata.net/task_manager/',
// },
// {
//   image_src: '/images/projects_webp/friends_corner.webp',
//   title: 'Friends Corner · Pet Project',
//   description:
//     'Developed a full-stack social platform featuring authentication, friend management, and real-time communication. Implemented WebSocket-based chat functionality, PostgreSQL data storage, backend APIs, and user management systems while focusing on scalable application architecture.',
//   period: '11.2023 - 12.2023',
//   position: 'Fullstack Developer',
//   github: 'https://github.com/CHchisho/Friends_Corner',
// },

// {
//   image_src: '/images/projects_webp/book_flex.webp',
//   title: 'BookFlex',
//   description:
//     'Developed a modern interface for an online booking platform for hotels, coworking spaces, and sports facilities. Implemented a convenient search and filtering system, integration with a map, and adaptive layout for different devices. Ensured integration with backend API and optimized performance of the client part.',
//   organization: 'Private Company',
//   period: '01.2025',
//   position: 'Frontend Engineer'
// },

// {
//   image_src: '/images/projects_webp/gear_hub.webp',
//   title: 'GearHub',
//   description:
//     'Implemented a search for equipment on the map, integrated an interactive map with points of rental, and developed an online booking system.',
//   organization: 'Private Company',
//   period: '04.2024 - 05.2024',
//   position: 'Frontend Developer'
// }
